// Modal de variantes de un accesorio: cada variante con su existencia, precio y − cantidad +.
// Cada toque agrega o quita UNA pieza; «Listo» cierra.
import { Button, Modal } from '@/kit';
import {
    accessoryVariantLabel,
    variantCountLabel,
    type ModelGroup,
} from '@/domains/sales/portal/catalog/lib/modelGroups';
import { maxQtyFor } from '../cartLines';
import type { CardSaleBenefits } from '../promotionSlots';
import type { CatalogItem } from '../types';
import { ExtraPrice, groupDisplayName, netPriceOf } from './ExtrasTable';
import { ExtraQuantityStepper, type ExtraPieceAction } from './ExtraQuantityStepper';
import { StockChip } from './StockChip';

export function AccessoryVariantsModal({
    group,
    counts,
    pendingOf,
    benefits,
    onAdd,
    onRemove,
    onClose,
}: {
    /** Modelo abierto; `null` cierra el modal. */
    group: ModelGroup | null;
    counts: ReadonlyMap<string, number>;
    pendingOf: (sku: string) => ExtraPieceAction | null;
    benefits: (item: CatalogItem) => CardSaleBenefits | undefined;
    onAdd: (sku: string) => void;
    onRemove: (sku: string) => void;
    onClose: () => void;
}) {
    if (group === null) return null;
    const name = groupDisplayName(group);
    const samePrice = group.minPriceCents === group.maxPriceCents;
    return (
        <Modal isOpen onClose={onClose} title={name} description={variantCountLabel(group)}>
            <ul className="-mx-1 flex flex-col divide-y divide-border">
                {group.variants.map((variant) => {
                    const label = accessoryVariantLabel(variant) || variant.sku;
                    const count = counts.get(variant.sku) ?? 0;
                    const net = netPriceOf(variant, benefits(variant));
                    return (
                        <li
                            key={variant.sku}
                            data-testid={`variant-${variant.sku}`}
                            className="flex h-16 items-center gap-3 px-1"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-[13px] font-semibold" title={label}>
                                    {label}
                                </p>
                                <StockChip item={variant} className="mt-1" />
                            </div>
                            {(!samePrice || net < variant.cashPriceCents) && (
                                <div className="w-24 text-right text-[12px] tabular-nums">
                                    <ExtraPrice listCents={variant.cashPriceCents} netCents={net} />
                                </div>
                            )}
                            <ExtraQuantityStepper
                                sku={variant.sku}
                                name={`${name} ${label}`}
                                count={count}
                                pending={pendingOf(variant.sku)}
                                canAdd={count < maxQtyFor(variant)}
                                onAdd={() => {
                                    onAdd(variant.sku);
                                }}
                                onRemove={() => {
                                    onRemove(variant.sku);
                                }}
                            />
                        </li>
                    );
                })}
            </ul>
            <div className="mt-4 flex justify-end">
                <Button onClick={onClose}>Listo</Button>
            </div>
        </Modal>
    );
}
